import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../utils/api';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';

// StackManager (SS1) — the user's standing supplement + medication stack.
//
// A stack item is a *definition* ("what I take"), not an intake event — logging
// it as an event is SS0 templates / the SS2 daily confirm. Here you just curate
// the list. Supplements can be picked from the honest catalog (which carries an
// evidence grade + a structure/function claim + a food-first note); medications
// are free-text and flag-and-defer only — we store them to check interactions
// later, never to advise a dose.

const inputCls =
  'flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm ' +
  'placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:border-surface-dark-border ' +
  'dark:bg-surface-dark-raised/50 dark:text-white dark:ring-offset-gray-900 ' +
  'dark:placeholder:text-gray-400 dark:focus-visible:ring-primary-400';

const gradeVariant = {
  strong: 'success',
  moderate: 'primary',
  preliminary: 'warning',
  theoretical: 'default',
};

const EvidenceBadge = ({ grade }) =>
  grade ? (
    <Badge variant={gradeVariant[grade] || 'default'} title={`Evidence: ${grade}`}>
      {grade} evidence
    </Badge>
  ) : null;

const doseText = (it) =>
  [it.dose, it.dose_unit].filter((x) => x !== null && x !== '' && x !== undefined).join(' ');

const StackManager = () => {
  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Add-supplement form
  const [suppSlug, setSuppSlug] = useState('');
  const [suppName, setSuppName] = useState('');
  const [suppDose, setSuppDose] = useState('');
  const [suppUnit, setSuppUnit] = useState('');

  // Add-medication form
  const [medOpen, setMedOpen] = useState(false);
  const [medName, setMedName] = useState('');
  const [medDose, setMedDose] = useState('');
  const [medUnit, setMedUnit] = useState('');
  const [medPrescribed, setMedPrescribed] = useState(true);

  const catalogBySlug = useMemo(
    () => Object.fromEntries(catalog.map((c) => [c.slug, c])),
    [catalog]
  );

  const load = async () => {
    try {
      const [cat, reg] = await Promise.all([
        apiClient.get('/api/supplements/catalog'),
        apiClient.get('/api/regimen'),
      ]);
      setCatalog(cat.data || []);
      setItems(reg.data || []);
    } catch (e) {
      setError('Could not load your stack.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const supplements = items.filter((i) => i.kind === 'supplement');
  const medications = items.filter((i) => i.kind === 'medication');

  // When a catalog supplement is picked, prefill dose/unit/name.
  const pickCatalog = (slug) => {
    setSuppSlug(slug);
    const c = catalogBySlug[slug];
    if (c) {
      setSuppName(c.name);
      setSuppDose(c.default_dose ?? '');
      setSuppUnit(c.default_dose_unit || '');
    }
  };

  const addSupplement = async () => {
    const name = (suppName || catalogBySlug[suppSlug]?.name || '').trim();
    if (!name) {
      setError('Pick a supplement or type a name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/regimen', {
        kind: 'supplement',
        catalog_slug: suppSlug || null,
        name,
        dose: suppDose === '' ? null : Number(suppDose),
        dose_unit: suppUnit || null,
      });
      setSuppSlug(''); setSuppName(''); setSuppDose(''); setSuppUnit('');
      load();
    } catch (e) {
      setError('Could not add that supplement.');
    } finally {
      setBusy(false);
    }
  };

  const addMedication = async () => {
    if (!medName.trim()) {
      setError('Enter a medication name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/regimen', {
        kind: 'medication',
        name: medName.trim(),
        dose: medDose === '' ? null : Number(medDose),
        dose_unit: medUnit || null,
        prescribed: medPrescribed,
      });
      setMedName(''); setMedDose(''); setMedUnit(''); setMedPrescribed(true);
      setMedOpen(false);
      load();
    } catch (e) {
      setError('Could not add that medication.');
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Remove ${it.name} from your stack?`)) return;
    try {
      await apiClient.delete(`/api/regimen/${it.id}`);
      load();
    } catch (e) {
      setError('Could not remove that item.');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading your stack…</p>;
  }

  return (
    <div className="space-y-8">
      {error && <p className="text-sm text-off-strong dark:text-off">{error}</p>}

      {/* Supplements */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Supplements</h2>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            What you take, with an honest read on the evidence behind each one.
          </p>
        </div>

        {supplements.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing here yet.</p>
        ) : (
          <ul className="space-y-2">
            {supplements.map((it) => {
              const c = it.catalog_slug ? catalogBySlug[it.catalog_slug] : null;
              return (
                <li
                  key={it.id}
                  className="rounded-lg border border-surface-border dark:border-surface-dark-border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {it.name}
                        </span>
                        {doseText(it) && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {doseText(it)}
                          </span>
                        )}
                        {c && <EvidenceBadge grade={c.evidence_grade} />}
                      </div>
                      {c?.structure_function_claim && (
                        <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
                          {c.structure_function_claim}
                        </p>
                      )}
                      {c?.food_first_note && (
                        <p className="mt-1 text-sm text-good-strong dark:text-good">
                          Food first: {c.food_first_note}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(it)}
                      className="text-sm text-gray-400 hover:text-off-strong px-1 shrink-0"
                      aria-label={`Remove ${it.name}`}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Add supplement */}
        <div className="rounded-lg border border-dashed border-surface-border dark:border-surface-dark-border p-3 space-y-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Add a supplement</span>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${inputCls} w-auto flex-1 min-w-[10rem]`}
              value={suppSlug}
              onChange={(e) => pickCatalog(e.target.value)}
            >
              <option value="">From the catalog…</option>
              {catalog.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name} · {c.evidence_grade} evidence
                </option>
              ))}
            </select>
            <input
              className={`${inputCls} w-auto flex-1 min-w-[8rem]`}
              placeholder="or type your own"
              value={suppSlug ? '' : suppName}
              onChange={(e) => { setSuppSlug(''); setSuppName(e.target.value); }}
            />
            <input
              className={`${inputCls} w-20`}
              placeholder="dose"
              inputMode="decimal"
              value={suppDose}
              onChange={(e) => setSuppDose(e.target.value)}
            />
            <input
              className={`${inputCls} w-20`}
              placeholder="unit"
              value={suppUnit}
              onChange={(e) => setSuppUnit(e.target.value)}
            />
            <Button size="sm" onClick={addSupplement} disabled={busy}>Add</Button>
          </div>
          {suppSlug && catalogBySlug[suppSlug]?.food_first_note && (
            <p className="text-sm text-good-strong dark:text-good">
              Food first: {catalogBySlug[suppSlug].food_first_note}
            </p>
          )}
        </div>
      </section>

      {/* Medications */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Medications</h2>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            We keep these on file only to flag possible interactions — we never suggest
            changing a medication. Always check with your doctor or pharmacist.
          </p>
        </div>

        {medications.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">None on file.</p>
        ) : (
          <ul className="space-y-2">
            {medications.map((it) => (
              <li
                key={it.id}
                className="rounded-lg border border-surface-border dark:border-surface-dark-border p-3 flex items-center justify-between gap-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{it.name}</span>
                  {doseText(it) && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">{doseText(it)}</span>
                  )}
                  {it.prescribed && <Badge variant="secondary">prescribed</Badge>}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(it)}
                  className="text-sm text-gray-400 hover:text-off-strong px-1 shrink-0"
                  aria-label={`Remove ${it.name}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {medOpen ? (
          <div className="rounded-lg border border-dashed border-surface-border dark:border-surface-dark-border p-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={`${inputCls} w-auto flex-1 min-w-[10rem]`}
                placeholder="medication name"
                value={medName}
                onChange={(e) => setMedName(e.target.value)}
              />
              <input
                className={`${inputCls} w-20`}
                placeholder="dose"
                inputMode="decimal"
                value={medDose}
                onChange={(e) => setMedDose(e.target.value)}
              />
              <input
                className={`${inputCls} w-20`}
                placeholder="unit"
                value={medUnit}
                onChange={(e) => setMedUnit(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary-600"
                checked={medPrescribed}
                onChange={(e) => setMedPrescribed(e.target.checked)}
              />
              Prescribed by a doctor
            </label>
            <div className="flex gap-2">
              <Button size="sm" onClick={addMedication} disabled={busy}>Add medication</Button>
              <Button size="sm" variant="ghost" onClick={() => setMedOpen(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => { setMedOpen(true); setError(''); }}>
            + Add a medication
          </Button>
        )}
      </section>

      <p className="text-sm text-gray-500 dark:text-gray-400 border-t border-surface-border dark:border-surface-dark-border pt-4">
        These statements have not been evaluated by the FDA. Supplements are not intended to
        diagnose, treat, cure, or prevent any disease. This is wellness guidance, not medical advice.
      </p>
    </div>
  );
};

export default StackManager;
